import { QueryFunctionContext, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { SignalRGroup } from "src/components";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import {
  FetchError,
  GetUserStrategicOwnershipDto,
  queryKeys,
  UrlUtilities,
  useAccessToken,
  type QueryParams,
} from "src/http";

const useGetUserStrategicOwnershipRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function getUserStrategicOwnershipRequest({
    queryKey,
  }: QueryFunctionContext<
    ReturnType<typeof queryKeys.actionItems.userOwnershipListWithOptions>
  >): Promise<GetUserStrategicOwnershipDto> {
    const accessToken = await getAccessToken();
    const [{ userId, queryParams }] = queryKey;

    const url = new URL(`${configuration.apiRootUrl}/users/${userId}/strategic-ownership`);
    UrlUtilities.appendQueryParams(url, queryParams);

    const response = await fetch(url.href, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getUserStrategicOwnershipRequest };
};

const useGetUserStrategicOwnership = (userId: string | undefined, queryParams?: QueryParams) => {
  const { getUserStrategicOwnershipRequest } = useGetUserStrategicOwnershipRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  useEffect(() => {
    if (userId !== undefined) {
      signalR.joinGroup(SignalRGroup.UserStrategicOwnershipItems(userId));
      return () => signalR.leaveGroup(SignalRGroup.UserStrategicOwnershipItems(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const handleShouldRefetchUserStrategicOwnershipItems = (userId: string) => {
      queryClient.invalidateQueries(queryKeys.actionItems.userOwnershipList(userId));
    };

    signalR.onShouldRefetchUserStrategicOwnershipItems(handleShouldRefetchUserStrategicOwnershipItems);
    return () => signalR.offShouldRefetchUserStrategicOwnershipItems(handleShouldRefetchUserStrategicOwnershipItems);
  }, [queryClient, signalR]);

  return useQuery({
    queryKey: queryKeys.actionItems.userOwnershipListWithOptions(userId, queryParams),
    queryFn: getUserStrategicOwnershipRequest,
    enabled: userId !== undefined,
    onSuccess: (data) => {
      data.actionItems.forEach((actionItem) => {
        queryClient.setQueryData(queryKeys.actionItems.summary(actionItem.guid), actionItem);
      });
    },
  });
};

export { useGetUserStrategicOwnership };
